import React, { useState } from 'react';
import { Place as PlaceIcon, Map as MapIcon, Edit as EditIcon, Person as PersonIcon, LocationOn as LocationIcon } from '@mui/icons-material';
import { Button, Box, Typography, Paper, Chip, Link } from '@mui/material';
import { Location } from '../types/Location';
import { LocationDialog } from './LocationDialog';
import { useAuth } from '../contexts/AuthContext';
import ResponsiveTitle from './ResponsiveTitle';

interface LocationDetailsViewProps {
    location: Location;
}

export const LocationDetailsView: React.FC<LocationDetailsViewProps> = ({ location }) => {
    const { isAdmin } = useAuth();
    const [isDialogOpen, setIsDialogOpen] = useState(false);

    return (
        <>
            {/* Header avec Titre et Actions regroupés */}
            <Box sx={{ mb: 4 }}>
                <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    mb: 1,
                    bgcolor: 'rgba(0, 0, 0, 0.5)',
                    borderRadius: 4,
                    px: 3,
                    py: 1,
                    color: 'white',
                    width: 'fit-content',
                    maxWidth: '100%' // Ensure container doesn't overflow
                }}>
                    <PlaceIcon sx={{ fontSize: 40, color: 'primary.main', flexShrink: 0 }} />
                    <ResponsiveTitle variant="h4" minFontSize={20}>
                        {location.name}
                    </ResponsiveTitle>
                </Box>

                <Link
                    href={location.googleMapsUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    underline="none"
                    sx={{
                        ml: { xs: 0, md: 7 },
                        mb: 2,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        color: 'text.primary',
                        width: 'fit-content'
                    }}
                >
                    <LocationIcon color="action" />
                    <Typography
                        variant="body2"
                        fontWeight="medium"
                        sx={{
                            textDecoration: 'underline',
                            textDecorationStyle: 'dotted',
                            textUnderlineOffset: '4px',
                            '&:hover': { color: 'primary.main' }
                        }}
                    >
                        Voir sur la carte
                    </Typography>
                </Link>

                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, ml: { xs: 0, md: 7 } }}>
                    {location.maxPlayers && (
                        <Chip
                            icon={<PersonIcon />}
                            label={`${location.maxPlayers} Joueurs Max`}
                            color="primary"
                            variant="outlined"
                        />
                    )}

                    {isAdmin && (
                        <Button
                            variant="contained"
                            startIcon={<EditIcon />}
                            onClick={() => setIsDialogOpen(true)}
                        >
                            Modifier
                        </Button>
                    )}
                </Box>
            </Box>

            {/* Photo de couverture */}
            {location.coverPhotoUrl && (
                <Paper
                    elevation={4}
                    sx={{
                        width: '100%',
                        height: { xs: 200, md: 400 },
                        backgroundImage: `url(${location.coverPhotoUrl})`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        borderRadius: 2,
                        mb: 4
                    }}
                />
            )}

            {/* Titre Section Infos */}
            <Typography variant="h5" gutterBottom sx={{ mb: 2, mt: 4 }}>
                Informations sur la salle
            </Typography>

            {/* Note Technique (Admin Only) */}
            {isAdmin && location.adminNotes && (
                <Paper sx={{ p: 3, mb: 4, bgcolor: 'rgba(255, 152, 0, 0.1)', border: '1px solid #ff9800' }}>
                    <Typography variant="h6" color="warning.main" gutterBottom>
                        🔒 Note Technique (Admin)
                    </Typography>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                        {location.adminNotes}
                    </Typography>
                </Paper>
            )}

            {/* Description */}
            {location.description && (
                <Paper sx={{ p: 3, mb: 4, bgcolor: 'rgba(23, 42, 69, 0.8)' }}>
                    <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>
                        {location.description}
                    </Typography>
                </Paper>
            )}

            {/* Galerie Scénarisée */}
            {location.gallery && location.gallery.length > 0 && (
                <Box>
                    <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
                        Accès et Détails
                    </Typography>

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        {location.gallery.map((step, index) => (
                            <Paper key={index} sx={{ overflow: 'hidden', bgcolor: 'rgba(10, 25, 47, 0.6)' }}>
                                {step.url && (
                                    <Box
                                        component="img"
                                        src={step.url}
                                        alt={`Etape ${index + 1}`}
                                        sx={{
                                            width: '100%',
                                            height: 'auto',
                                            maxHeight: 800,
                                            objectFit: 'contain',
                                            display: 'block',
                                            bgcolor: 'black'
                                        }}
                                    />
                                )}
                                {step.description && (
                                    <Box sx={{ p: 3 }}>
                                        <Typography variant="body1" sx={{ fontSize: '1.2rem' }}>
                                            {step.description}
                                        </Typography>
                                    </Box>
                                )}
                            </Paper>
                        ))}
                    </Box>
                </Box>
            )}

            {/* Dialog d'édition */}
            <LocationDialog
                open={isDialogOpen}
                onClose={() => setIsDialogOpen(false)}
                locationToEdit={location}
            />
        </>
    );
};
